import React, { useState, useEffect } from 'react'
import {
    Typography,
    TextField,
    FormControlLabel,
    RadioGroup,
    Radio,
    InputAdornment,
} from '@mui/material'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { SearchableSelect } from 'components'
import { CaseBuilderIssuerFormLabel } from './index'
import { TFilterOption } from 'components/CaseBuilderIssuer/CaseBuilderIssuer.vm'
import { NumericFormat } from 'react-number-format'
import getSymbolFromCurrency from 'currency-symbol-map'
import { FormHelperText } from '@mui/material'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'

interface ITransactionInfoStep2Props {
    currencyList: TFilterOption[]
    disputeReasonList: TFilterOption[]
    isLoadingCurrencyList: boolean
    formInfo: { [key: string]: any }
    handleChangeFormField: (accessor: string, input: any) => void
    setSelectedDisputeReasonId: (selectedId: number | null) => void
    setEnableSaveAndContinue: (enable: boolean) => void

    className?: string
}

const CustomFormatAmount = React.forwardRef<HTMLInputElement, any>(
    function CustomNumericFormat(props, ref) {
        const { onChange, ...other } = props

        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values: any) =>
                    onChange({ target: { value: values.value } })
                }
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                allowLeadingZeros={false}
            />
        )
    }
)

export const TransactionInfoStep2 = ({
    currencyList,
    isLoadingCurrencyList,
    disputeReasonList,
    formInfo,
    handleChangeFormField,
    setSelectedDisputeReasonId,
    setEnableSaveAndContinue,
    className = '',
}: ITransactionInfoStep2Props) => {
    const { clientTheme } = useThemeStyling()
    const { user } = useAuthedUser()
    const showClientTypeIssuerUI = user?.is_issuer ?? false

    const [radioValue, setRadioValue] = useState<string>('full')
    const [fieldBuilderErrors, setFieldBuilderErrors] = useState<{
        [fieldId: number]: string
    }>({})
    const stylesObj = {
        textInputField: {
            borderRadius: clientTheme.selectionBox.borderRadius,
            '& > div input': {
                padding: '7px',
            },
            '& > .MuiSelect-select': {
                padding: '7px',
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: clientTheme.selectionBox.borderRadius,
            },
            '& .MuiInputBase-input': {
                color: clientTheme.typography.fontColor.primaryText,
            },
        },
        textInputMultiField: {
            borderRadius: clientTheme.selectionBox.borderRadius,
            '& .MuiOutlinedInput-root': {
                borderRadius: clientTheme.selectionBox.borderRadius,
                padding: '7px',
            },
            '& .MuiInputBase-input': {
                color: clientTheme.typography.fontColor.primaryText,
                // overflowY: 'scroll',
            },
        },
    }

    useEffect(() => {
        const enableBtn = formInfo.disputeReason
        setEnableSaveAndContinue(enableBtn)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formInfo])

    useEffect(() => {
        if (
            Number(formInfo.transactionAmount) < Number(formInfo.disputeAmount)
        ) {
            setFieldBuilderErrors({
                ...fieldBuilderErrors,
                129: `Invalid amount entered: The dispute amount cannot be greater than the transaction amount of ${
                    formInfo.currency
                        ? getSymbolFromCurrency(formInfo.currency) ??
                          formInfo.currency
                        : ''
                } ${formInfo.transactionAmount || '0.00'}.`,
            })
        } else {
            const newObj = {
                ...fieldBuilderErrors,
            }
            delete newObj[129]
            setFieldBuilderErrors(newObj)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formInfo])

    return (
        <div className={`emp-transactionInfoStep2-root ${className}`}>
            {/* section 1 */}
            <div className="emp-transactionInfoStep2-section">
                <div>
                    <Typography className="emp-transactionInfoStep2-header">
                        Transaction Information
                    </Typography>
                    <Typography className="emp-transactionInfoStep2-subHeader">
                        Enter key details like merchant name, amounts, dates,
                        and authorization data.
                    </Typography>
                </div>
                {/* row 1 */}
                <div className="emp-transactionInfoStep2-formFields">
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Merchant Name'}
                        />
                        <div>
                            <TextField
                                variant={'outlined'}
                                sx={stylesObj.textInputField}
                                required={false}
                                type="text"
                                name={'merchantName'}
                                fullWidth
                                placeholder="Merchant name"
                                value={formInfo.merchantName ?? ''}
                                onChange={(e) => {
                                    handleChangeFormField(
                                        'merchantName',
                                        e?.target.value
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Merchant Descriptor'}
                        />
                        <div>
                            <TextField
                                variant={'outlined'}
                                sx={stylesObj.textInputField}
                                required={false}
                                type="text"
                                name={'merchantDescriptor'}
                                fullWidth
                                placeholder="Merchant descriptor"
                                value={formInfo.merchantDescriptor ?? ''}
                                onChange={(e) => {
                                    handleChangeFormField(
                                        'merchantDescriptor',
                                        e?.target.value
                                    )
                                }}
                            />
                        </div>
                    </div>
                </div>
                {/* row 2 */}
                <div className="emp-transactionInfoStep2-formFields">
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Trans. Amount'}
                        />
                        <div>
                            <TextField
                                variant={'outlined'}
                                sx={stylesObj.textInputField}
                                required={false}
                                type="text"
                                name={'transactionAmount'}
                                fullWidth
                                placeholder="0.00"
                                value={formInfo.transactionAmount ?? ''}
                                onChange={(e) => {
                                    handleChangeFormField(
                                        'transactionAmount',
                                        e?.target.value
                                    )
                                }}
                                InputProps={{
                                    inputComponent: CustomFormatAmount,
                                    ...(formInfo.currency && {
                                        startAdornment: (
                                            <InputAdornment
                                                position="start"
                                                className="emp-renderTextField-startAdornment"
                                            >
                                                <div>
                                                    {getSymbolFromCurrency(
                                                        formInfo.currency
                                                    ) ?? formInfo.currency}
                                                </div>
                                            </InputAdornment>
                                        ),
                                    }),
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Trans. Currency'}
                        />
                        <div>
                            <SearchableSelect
                                // placeholder={'$'}
                                value={formInfo.currency ?? ''}
                                onValueChange={(e) => {
                                    handleChangeFormField('currency', e?.name)
                                }}
                                options={currencyList ?? []}
                                accessor={'name'}
                                testId={'currency'}
                                hideSearch
                                selectFontSize={
                                    clientTheme.typography.body1.fontSize
                                }
                                isPaginate={false}
                                loadingValues={isLoadingCurrencyList}
                            />
                        </div>
                    </div>
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'BIN (First 6 digits)'}
                        />
                        <div>
                            <TextField
                                variant={'outlined'}
                                sx={stylesObj.textInputField}
                                required={false}
                                type="text"
                                name={'binFirst6digits'}
                                fullWidth
                                placeholder="000000"
                                value={formInfo.binFirst6digits ?? ''}
                                onChange={(e) => {
                                    if (e?.target.value.length > 6) return
                                    handleChangeFormField(
                                        'binFirst6digits',
                                        e?.target.value
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'BIN (Last 4 digits)'}
                        />
                        <div>
                            <TextField
                                variant={'outlined'}
                                sx={stylesObj.textInputField}
                                required={false}
                                type="text"
                                name={'binLast4Digits'}
                                fullWidth
                                placeholder="0000"
                                value={formInfo.binLast4Digits ?? ''}
                                onChange={(e) => {
                                    if (e?.target.value.length > 4) return
                                    handleChangeFormField(
                                        'binLast4Digits',
                                        e?.target.value
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <CaseBuilderIssuerFormLabel labelText={'Auth ID'} />
                        <div>
                            <TextField
                                variant={'outlined'}
                                sx={stylesObj.textInputField}
                                required={false}
                                type="text"
                                name={'authId'}
                                fullWidth
                                placeholder={'Unauthorized'}
                                value={formInfo.authId ?? ''}
                                onChange={(e) => {
                                    handleChangeFormField(
                                        'authId',
                                        e?.target.value
                                    )
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* row 3 */}
                <div className="emp-transactionInfoStep2-formFields">
                    <div
                        className="emp-transactionInfoStep2-formFields"
                        style={{ flexDirection: 'column' }}
                    >
                        {showClientTypeIssuerUI && (
                            <div>
                                <CaseBuilderIssuerFormLabel labelText="ARN" />
                                <TextField
                                    variant="outlined"
                                    sx={stylesObj.textInputField}
                                    type="text"
                                    name="arn"
                                    fullWidth
                                    placeholder="ARN"
                                    value={formInfo.arn ?? ''}
                                    onChange={(e) => {
                                        handleChangeFormField(
                                            'arn',
                                            e?.target.value
                                        )
                                    }}
                                />
                            </div>
                        )}

                        <div>
                            <CaseBuilderIssuerFormLabel
                                labelText={'Dispute Reason'}
                                isRequired
                            />

                            <div>
                                <SearchableSelect
                                    value={formInfo.disputeReason ?? ''}
                                    placeholder={'Select dispute reason'}
                                    onValueChange={(e) => {
                                        setSelectedDisputeReasonId(
                                            e?.id ?? null
                                        )
                                        handleChangeFormField(
                                            'disputeReason',
                                            e?.name
                                        )
                                    }}
                                    options={disputeReasonList ?? []}
                                    accessor={'name'}
                                    testId={'disputeReason'}
                                    hideSearch
                                    selectFontSize={
                                        clientTheme.typography.body1.fontSize
                                    }
                                    isPaginate={false}
                                />
                            </div>
                        </div>
                        <div>
                            <CaseBuilderIssuerFormLabel
                                labelText={'Dispute Amount'}
                            />

                            <div>
                                <RadioGroup
                                    value={radioValue}
                                    onChange={(e) => {
                                        setRadioValue(e.target.value)
                                    }}
                                    className="emp-transactionInfoStep2-radioGrp"
                                >
                                    <div className="emp-transactionInfoStep2-radioContainer">
                                        <FormControlLabel
                                            value={'full'}
                                            control={
                                                <Radio
                                                    size={'small'}
                                                    color="secondary"
                                                />
                                            }
                                            label="Full purchase amount"
                                        />
                                        <TextField
                                            variant={'outlined'}
                                            sx={stylesObj.textInputField}
                                            required={false}
                                            type="text"
                                            name={'fullAmount'}
                                            fullWidth
                                            placeholder="0.00"
                                            value={
                                                formInfo.transactionAmount ?? ''
                                            }
                                            disabled
                                            InputProps={{
                                                inputComponent: CustomFormatAmount,
                                                ...(formInfo.currency && {
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            className="emp-renderTextField-startAdornment"
                                                        >
                                                            <div>
                                                                {getSymbolFromCurrency(
                                                                    formInfo.currency
                                                                ) ??
                                                                    formInfo.currency}
                                                            </div>
                                                        </InputAdornment>
                                                    ),
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div className="emp-transactionInfoStep2-radioContainer">
                                        <FormControlLabel
                                            value={'other'}
                                            control={
                                                <Radio
                                                    size={'small'}
                                                    color="secondary"
                                                />
                                            }
                                            label="Other amount"
                                        />
                                        <TextField
                                            variant={'outlined'}
                                            sx={stylesObj.textInputField}
                                            required={false}
                                            type="text"
                                            name={'otherAmount'}
                                            fullWidth
                                            placeholder="0.00"
                                            value={formInfo.disputeAmount ?? ''}
                                            onChange={(e) => {
                                                handleChangeFormField(
                                                    'disputeAmount',
                                                    e?.target.value
                                                )
                                            }}
                                            disabled={radioValue === 'full'}
                                            InputProps={{
                                                inputComponent: CustomFormatAmount,
                                                ...(formInfo.currency && {
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            className="emp-renderTextField-startAdornment"
                                                        >
                                                            <div>
                                                                {getSymbolFromCurrency(
                                                                    formInfo.currency
                                                                ) ??
                                                                    formInfo.currency}
                                                            </div>
                                                        </InputAdornment>
                                                    ),
                                                }),
                                            }}
                                        />
                                    </div>
                                </RadioGroup>
                                {Boolean(fieldBuilderErrors[129]) && (
                                    <FormHelperText
                                        className="emp-transactionInfoStep2-errorText"
                                        error
                                    >
                                        {fieldBuilderErrors[129]}
                                    </FormHelperText>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="emp-transactionInfoStep2-disputeDetails">
                        <CaseBuilderIssuerFormLabel
                            labelText={'Dispute Details'}
                        />
                        <TextField
                            variant={'outlined'}
                            sx={stylesObj.textInputMultiField}
                            required={false}
                            type="text"
                            name={'disputeDetails'}
                            fullWidth
                            placeholder="Please provide details why you're disputing this transaction"
                            multiline
                            minRows={8}
                            value={formInfo.disputeDetails ?? ''}
                            onChange={(e) => {
                                handleChangeFormField(
                                    'disputeDetails',
                                    e?.target.value
                                )
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
